import { useMemo, useState } from 'react'

import { DateRangePanel } from '@/components/lemmala-design-system/date-range-panel/date-range-panel'
import { DropdownSelect } from '@/components/lemmala-design-system/dropdown/dropdown-select'
import { FloatingPopover } from '@/components/lemmala-design-system/popover/floating-popover'
import { AgFilterClearButton } from '@/components/lemmala-design-system/table/components/ag-filter-clear-button'
import { AgFilterRemoveButton } from '@/components/lemmala-design-system/table/components/ag-filter-remove-button'
import { AgFilterTrigger } from '@/components/lemmala-design-system/table/components/ag-filter-trigger'

import { CUSTOM_RANGE_ID } from './consts'
import { labels } from './labels'
import type { TimeframePreset } from './types'
import { encodeCustomRange, formatCustomRange, parseCustomRange } from './utils'

export type TimeframeFilterProps = {
  label: string
  presets: readonly TimeframePreset[]
  value: string
  onChange: (value: string) => void
  allowCustomRange?: boolean
  maxRangeYears?: number
  onClear?: () => void
  onRemove?: () => void
  defaultOpen?: boolean
  modal?: boolean
}

export const TimeframeFilter = ({
  label,
  presets,
  value,
  onChange,
  allowCustomRange = false,
  maxRangeYears,
  onClear,
  onRemove,
  defaultOpen = false,
  modal = false,
}: TimeframeFilterProps) => {
  const [listOpen, setListOpen] = useState(defaultOpen)
  const [pickingRange, setPickingRange] = useState(false)

  const preset = presets.find((option) => option.value === value)
  const customRange = preset || !allowCustomRange ? undefined : parseCustomRange(value)
  const selectedLabel = preset?.label ?? (customRange && formatCustomRange(customRange))
  const selectedId = preset?.value ?? (customRange && CUSTOM_RANGE_ID)
  const items = useMemo(
    () => [
      ...presets.map((option) => ({ id: option.value, label: option.label })),
      ...(allowCustomRange ? [{ id: CUSTOM_RANGE_ID, label: labels.customRange }] : []),
    ],
    [presets, allowCustomRange],
  )
  const selectedItems = useMemo(() => (selectedId ? [selectedId] : []), [selectedId])

  const handleItemSelect = (id: string) => {
    if (id === CUSTOM_RANGE_ID) setPickingRange(true)
    else onChange(id)
  }

  const list = (
    <DropdownSelect
      items={items}
      selectedItems={selectedItems}
      onItemSelect={handleItemSelect}
      open={listOpen}
      onOpenChange={setListOpen}
      isModal={modal}
      popoverAlign='start'
      trigger={
        <AgFilterTrigger
          label={label}
          value={selectedLabel}
          showChevron={!onRemove && (!selectedLabel || !onClear)}
          standalone={!onClear && !onRemove}
          attached={!!onRemove}
        />
      }
    />
  )

  return (
    <div className='flex items-start gap-px'>
      {/* Only anchors the range picker to the chip; it opens from the list's "Custom range". */}
      {allowCustomRange ? (
        <FloatingPopover
          open={pickingRange}
          onOpenChange={(next) => !next && setPickingRange(false)}
          modal={modal}
          align='start'
          sideOffset={2}
          showArrow={false}
          showClose={false}
          showCancelButton={false}
          showPrimaryButton={false}
          className='w-auto [&>div]:gap-0 [&>div]:p-0'
          trigger={<span className='inline-flex'>{list}</span>}
        >
          <DateRangePanel
            initialFrom={customRange?.from}
            initialTo={customRange?.to}
            maxYears={maxRangeYears}
            onCancel={() => setPickingRange(false)}
            onApply={(from, to) => {
              setPickingRange(false)
              if (from && to) onChange(encodeCustomRange({ from, to }))
            }}
          />
        </FloatingPopover>
      ) : (
        list
      )}
      {selectedLabel && onClear && !onRemove && <AgFilterClearButton onClear={onClear} />}
      {onRemove && <AgFilterRemoveButton hasSelection={!!selectedLabel} onRemove={onRemove} />}
    </div>
  )
}
